import api from 'api/instance';
import { FetchQueryType, FetchResponse } from 'types/common';
import { Status } from 'types/items';

import { Order } from 'types/order';
import { toMidnightISOString } from 'utils/dateUtils';

const URL = `${process.env.REACT_APP_API_URL}`;

export const fetchPendingDeliveries = async (params: FetchQueryType) => {
  const { data } = await api.get<FetchResponse<Order>>(`${URL}/order/list`, {
    params: {
      hasPendingDelivery: true,
      ...params,
    },
  });

  return data;
};

type CreateOrderPayload = {
  washHub: string;
  washHubCode: string;
  site: string;
  siteCode: string;
  deliveryAt: string;
  details: {
    casesCount: number;
    sku: string;
    skuCode: string;
  }[];
  dayOfWeek: number;
};

export const createOrder = async (payload: CreateOrderPayload) => {
  const { data } = await api.post(`${URL}/order/create`, payload);

  return data;
};

export const fetchOrders = async (
  params: FetchQueryType & {
    status?: Status;
    startDate?: Date;
    endDate?: Date;
    excludeStatus?: number[];
    venue?: string[];
    accountManagers?: string[];
    sortByField?: string;
    sort?: number;
    invoiced?: boolean;
    feeAdjustmentTypes?: string[];
    search?: string;
  },
  /** Aborts the request once react-query no longer needs it (e.g. a newer search). */
  signal?: AbortSignal,
) => {
  const endDate = toMidnightISOString(params.endDate);
  const startDate = toMidnightISOString(params.startDate);

  const { data } = await api.get<FetchResponse<Order>>(`${URL}/order/list`, {
    params: {
      ...params,
      startDate,
      endDate,
      ...(params.accountManagers && params.accountManagers.length
        ? { accountManagers: params.accountManagers }
        : {}),
    },
    signal,
  });

  return data;
};

export const deleteOrders = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/order/remove-many`, {
    data: { ids },
  });

  return data;
};

export const updateOrder = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(`${URL}/order/detail/${_id}`, _payload);

  return data;
};

export const fetchOrderHistory = async (id: string) => {
  const { data } = await api.get(`${URL}/order/order-transaction/${id}`);

  return data?.item;
};

export const getOrderCounts = async (
  washHub: string,
  initialStartDate?: Date,
  initialEndDate?: Date,
  venue?: string[],
  invoiced?: boolean,
) => {
  const endDate = toMidnightISOString(initialEndDate);
  const startDate = toMidnightISOString(initialStartDate);

  const { data } = await api.get(`${URL}/order/get-counts`, {
    params: {
      washHub,
      endDate,
      startDate,
      venue,
      invoiced,
    },
  });

  return data?.item;
};
export const exportOrdersCsv = async ({
  washHub,
  status,
  venue,
  invoiced,
  accountManagers,
  ...payload
}: {
  washHub: string;
  status?: number;
  startDate?: Date;
  endDate?: Date;
  venue?: string[];
  accountManagers?: string[];
  invoiced?: boolean;
}) => {
  const endDate = toMidnightISOString(payload.endDate);
  const startDate = toMidnightISOString(payload.startDate);

  const { data } = await api.post<any>(`${URL}/order/export-orders`, {
    washHub,
    status: status,
    startDate,
    endDate,
    venue,
    invoiced,
    accountManagers,
  });

  return data;
};

export const rejectOrder = async (id: string) => {
  const { data } = await api.patch(
    `${URL}/order/reject-request-order/${id}`,
    {},
  );

  return data;
};

export const acceptOrder = async (payload: any, id: string) => {
  const { data } = await api.patch(
    `${URL}/order/schedule-request-order/${id}`,
    payload,
  );

  return data;
};

export const fetchOrderDetail = async (portalId: string, orderId: string) => {
  const { data } = await api.get(
    `${URL}/customer-portal/order-details/${portalId}/${orderId}`,
  );

  return data?.item?.[0] as Order;
};

export const getOrderById = async (orderId: string) => {
  const { data } = await api.get(`${URL}/order/find-one/${orderId}`);

  return data?.item as Order;
};

export const getOrderImages = async (orderId: string) => {
  const { data } = await api.get(`${URL}/order/get-image-urls/${orderId}`);

  return data?.item as {
    signature?: string;
    proofOfDevliveries?: Array<string>;
  };
};

export const getPackagesBySKU = async (washHub: string) => {
  const { data } = await api.get(`${URL}/order/get-available-package-by-sku`, {
    params: {
      washHub,
    },
  });

  return data?.item as Array<{
    readyCount: number;
    requestedCount: number;
    packageSku: {
      _id: string;
      name: string;
      description: string;
      type: string;
      isDeleted: boolean;
      caseSku: string;
      items: Array<{
        sku: string;
        quantity: number;
        _id: string;
      }>;
      isSuspended: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }>;
};

export const markAsInvoiced = async ({
  status,
  orderId,
  invoiced,
  cokeAdjustment,
  feeAdjustment,
  feeAdjustments,
  adjustmentMemo,
}: {
  orderId: string;
  status?: Status;
  invoiced?: boolean;
  feeAdjustment?: number;
  feeAdjustments?: Array<{ type: string; amount: number }>;
  cokeAdjustment?: number;
  adjustmentMemo?: string;
}) => {
  const { data } = await api.post(`${URL}/order/mark-as-invoiced/${orderId}`, {
    status,
    invoiced,
    cokeAdjustment,
    feeAdjustment,
    feeAdjustments,
    adjustmentMemo,
  });

  return data;
};

export const getPackageItems = async (orderId: string) => {
  const { data } = await api.get(
    `${URL}/order/get-item-sku-summary/${orderId}`,
  );

  return data.item as any[];
};

export const refreshPrice = async (orderId: string) => {
  const { data } = await api.patch(
    `${URL}/order/refresh-pricing-details/${orderId}`,
  );

  return data.item;
};
